import { type ReactElement, type ReactNode, useContext, useMemo } from 'react';
import cx from 'clsx';
import {
  FieldError,
  FieldErrorContext,
  type FieldErrorProps,
  TextContext,
} from 'react-aria-components';
import IconCrossRoundedFilledSmall from '@repo/monochrome-icons/icon-cross-rounded-filled-small';
import styles from './error-message.module.css';

export interface ErrorMessageProps extends FieldErrorProps {
  /** Whether the field is currently invalid. Combined with the resolved message to decide
   * whether to show the icon — a field can be invalid while its message resolves to empty
   * (e.g. a validator function that only handles some validity states). */
  isInvalid?: boolean;
  /** Adds role="alert" alongside the default aria-live region, for consumers relying on that role (e.g. Checkbox, for backward compatibility). */
  alertRole?: boolean;
}

// Only used as a placeholder when there's no ambient FieldErrorContext to read real
// validity from (e.g. Checkbox, which has no native constraint validation of its own).
const EMPTY_VALIDITY_STATE: ValidityState = {
  badInput: false,
  customError: false,
  patternMismatch: false,
  rangeOverflow: false,
  rangeUnderflow: false,
  stepMismatch: false,
  tooLong: false,
  tooShort: false,
  typeMismatch: false,
  valueMissing: false,
  valid: true,
};

// A TextContext without a `slots` map: Text then skips slot validation entirely.
const EMPTY_TEXT_CONTEXT = {};

export function ErrorMessage({
  className,
  isInvalid,
  alertRole,
  children,
  ...props
}: ErrorMessageProps): ReactElement {
  // FieldError only renders while a FieldErrorContext reports isInvalid; text/select/date/etc.
  // fields provide one natively, but some consumers (e.g. Checkbox) don't. Falling back to a
  // synthetic context built from our own isInvalid prop makes ErrorMessage work the same either
  // way, without each such consumer having to wire this up itself.
  const ambientValidation = useContext(FieldErrorContext);
  const ambientTextContext = useContext(TextContext);
  const fallbackValidation = useMemo(
    () => ({
      isInvalid: Boolean(isInvalid),
      validationErrors: [],
      validationDetails: EMPTY_VALIDITY_STATE,
    }),
    [isInvalid]
  );

  const validation = ambientValidation ?? fallbackValidation;
  const hasAmbientValidation = ambientValidation !== null;

  // `children` can be a validator function that returns '' for validity states it doesn't
  // handle — it's truthy as a function reference regardless of what it evaluates to, so
  // resolving it here (rather than trusting the caller's `Boolean(errorMessage)`) is what
  // keeps the icon from showing next to no visible text.
  const resolvedMessage: ReactNode =
    typeof children === 'function'
      ? children({ ...validation, defaultChildren: undefined })
      : children;
  const showIcon = validation.isInvalid && Boolean(resolvedMessage);

  return (
    // FieldError strips role/aria-live via filterDOMProps, so a live-region wrapper is
    // needed to announce errors on appearance (WCAG 4.1.3). aria-live (not role="alert")
    // avoids declaring every field error an alert widget, which collides with page-level
    // getByRole('alert') in consumers. `display: contents` keeps it out of the field's flex
    // layout when there's no error to show (no phantom gap); when showIcon, it becomes the
    // flex row for the icon + message instead.
    <span
      aria-atomic="true"
      aria-live="assertive"
      className={cx(styles.liveRegion, showIcon && styles.errorMessage)}
      role={showIcon && alertRole ? 'alert' : undefined}
    >
      {showIcon && <IconCrossRoundedFilledSmall className={styles.errorIcon} />}
      <FieldErrorContext.Provider value={validation}>
        {/* FieldError renders its text through `Text slot="errorMessage"`, which throws when the
            nearest TextContext exposes a `slots` map without that slot. Real form fields provide a
            matching slot, but fallback consumers (e.g. Checkbox) don't — they'd otherwise inherit
            an unrelated ancestor's map (Dialog exposes only `title`/`description`) and crash. */}
        <TextContext.Provider
          value={hasAmbientValidation ? ambientTextContext : EMPTY_TEXT_CONTEXT}
        >
          <FieldError
            className={cx(className, styles.error, 'body-standard')}
            data-test-form-elements-message
            data-test-level="error"
            {...props}
          >
            {children}
          </FieldError>
        </TextContext.Provider>
      </FieldErrorContext.Provider>
    </span>
  );
}
